import {Component, Input, OnChanges} from '@angular/core';
import {UserResponse, CountryResponse} from '../../../../../../@shared/models/response.module';
import {UserService} from '../../../../../../@core/services/user.service';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {USM_USR_16, USM_USR_3} from '../../../../../../@shared/models/input.module';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {StaticService} from '../../../../../../@shared/services/static-service';

@Component({
  selector: 'app-edit-user-profile',
  standalone: false,
  templateUrl: './edit-user-profile.html',
  styleUrl: './edit-user-profile.css'
})
export class EditUserProfile implements OnChanges{
  @Input() user: UserResponse
  profileImage: File | null

  submitted = false;
  saving = false;
  countries: CountryResponse[] = [];

  constructor(private userService: UserService,
              private modal: NgbActiveModal,
              private toastService: ToastService,
              private translateService: TranslateService,
              private staticService: StaticService) {
  }

  // Validation helpers
  get isFirstNameInvalid(): boolean {
    return this.submitted && !this.user?.first_name?.trim();
  }

  get isLastNameInvalid(): boolean {
    return this.submitted && !this.user?.last_name?.trim();
  }

  get isEmailInvalid(): boolean {
    return this.submitted && !this.user?.email?.trim();
  }

  get isFormValid(): boolean {
    return !!(this.user?.first_name?.trim() && this.user?.last_name?.trim() && this.user?.email?.trim());
  }

  ngOnInit() {
    if (this.user) {
      this.user.date_of_birth = fromApiDate(this.user.date_of_birth);
    }
    this.loadCountries();
  }

  loadCountries() {
    this.staticService.getCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
      },
      error: (err) => {
        console.error('Failed to load countries', err);
      }
    });
  }

  ngOnChanges(SimpleChanges: any){
    if (this.user) {
      this.user.date_of_birth = fromApiDate(this.user.date_of_birth);
    }
  }

  saveProfile(){
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('userProfile.edit.validationError'));
      return;
    }

    this.saving = true;

    let params: USM_USR_3 = {
      user_id: this.user.user_id,
      first_name: this.user.first_name,
      last_name: this.user.last_name,
      email: this.user.email,
      phone: this.user.phone,
      date_of_birth: this.toISO(this.user.date_of_birth) ? this.user.date_of_birth : null,
      riding_club: this.user.riding_club ? this.user.riding_club : null,
      country: null,  // Legacy field - we now use country_id
      country_id: this.user.country_id ? this.user.country_id : null,
      address: this.user.address ? this.user.address : null,
      city: this.user.city ? this.user.city : null,
      state: this.user.state ? this.user.state : null,
      zip_code: this.user.zip_code ? this.user.zip_code : null
    }

    this.userService.updateUser(params).subscribe({
      next: (res) => {
        this.user = res

        if (this.profileImage) {
          let params: USM_USR_16 = {
            image: this.profileImage
          }
          this.userService.updateProfileImage(params).subscribe({
            next: (res) => {
              this.user = res
              this.saving = false;
              this.toastService.success(this.translateService.instant('userProfile.edit.successMessage'));
              this.modal.close(this.user)
            },
            error: (err) => {
              this.saving = false;
              this.toastService.error(err.error?.reason || this.translateService.instant('userProfile.edit.imageUploadError'));
            }
          })
        }
        else {
          this.saving = false;
          this.toastService.success(this.translateService.instant('userProfile.edit.successMessage'));
          this.modal.close(this.user)
        }
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || this.translateService.instant('userProfile.edit.updateError'));
      }
    });
  }

  private toISO(date: string): string {
    if (!date) return '';
    // Handle full ISO strings (2025-10-12T00:00:00.000Z)
    if (date.includes('T')) {
      return date.split('T')[0]; // → "2025-10-12"
    }
    // Already YYYY-MM-DD (from input type="date")
    return date;
  }
}

function fromApiDate(v?: string | null): string {
  if (!v) return ''; // always return a string
  try {
    const parsed = new Date(v);
    if (isNaN(parsed.getTime())) {
      const [datePart] = v.split(',');
      const [month, day, year] = datePart.split('/').map(x => x.trim());
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } catch {
    return '';
  }
}
